import { useState } from 'react';
import { T, useGT } from 'gt-react';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  PlusIcon,
  XIcon,
} from '@phosphor-icons/react';
import type { ColumnConfigEntry } from '@erp/shared-types';
import type { TaskTypeResponse } from '@erp/rpc';
import { Button } from 'src/components/ui/button';
import { Checkbox } from 'src/components/ui/checkbox';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import { Switch } from 'src/components/ui/switch';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'src/components/ui/popover';
import { TaskColumnPicker } from 'src/pages/authenticated/tasks/columns/TaskColumnPicker';
import { newCustomColumnId } from 'src/pages/authenticated/tasks/columns/taskColumnOptions';
import { buildTaskPageQueueColumns } from 'src/pages/authenticated/tasks/columns/taskPageColumns';
import { moveDefaultColumn, updateDefaultColumn } from './taskPageAdmin';

function DefaultColumnRow({
  column,
  index,
  count,
  onMove,
  onUpdate,
  onRemove,
}: {
  column: ColumnConfigEntry;
  index: number;
  count: number;
  onMove: (delta: -1 | 1) => void;
  onUpdate: (updates: Partial<Omit<ColumnConfigEntry, 'id'>>) => void;
  onRemove: () => void;
}) {
  const gt = useGT();
  return (
    <li className="flex items-center gap-1.5 rounded border px-2 py-1">
      <Checkbox
        checked={!column.hidden}
        onCheckedChange={(checked) => onUpdate({ hidden: checked !== true })}
        aria-label={gt('Show column {name} by default', {
          context: 'checkbox in the task page default columns editor',
          name: column.header,
        })}
      />
      <Input
        value={column.header}
        onChange={(e) => onUpdate({ header: e.target.value })}
        className="h-7 w-36 text-xs"
        aria-label={gt('Column header', {
          context: 'input in the task page default columns editor',
        })}
      />
      <span
        className="min-w-0 flex-1 truncate font-mono text-[11px] text-muted-foreground"
        title={column.apiField}
      >
        {column.apiField}
      </span>
      <Button
        variant="ghost"
        size="icon"
        className="size-6"
        disabled={index === 0}
        onClick={() => onMove(-1)}
        aria-label={gt('Move column up', {
          context: 'button in the task page default columns editor',
        })}
      >
        <ArrowUpIcon className="size-3" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="size-6"
        disabled={index === count - 1}
        onClick={() => onMove(1)}
        aria-label={gt('Move column down', {
          context: 'button in the task page default columns editor',
        })}
      >
        <ArrowDownIcon className="size-3" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="size-6"
        onClick={onRemove}
        aria-label={gt('Remove column {name}', {
          context: 'button, remove a custom column from the table',
          name: column.header,
        })}
      >
        <XIcon className="size-3" />
      </Button>
    </li>
  );
}

/**
 * Admin default queue columns for a task page. Null keeps the built-in set;
 * "Customize Columns" seeds an editable copy of it. Stored as
 * `ColumnConfigEntry[]` JSON so it can travel with an exported task type.
 */
export function TaskPageDefaultColumnsField({
  value,
  onChange,
  taskType,
}: {
  value: ColumnConfigEntry[] | null;
  onChange: (next: ColumnConfigEntry[] | null) => void;
  taskType: TaskTypeResponse | undefined;
}) {
  const gt = useGT();
  const [pickerOpen, setPickerOpen] = useState(false);

  function customizeFromBuiltIn() {
    onChange(
      buildTaskPageQueueColumns(
        gt('Anchor', {
          context:
            'task queue table column header for the business entity a task is working on',
        }),
      ),
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <Label>
        <T context="task page form field label">Default columns</T>
      </Label>
      {value === null ? (
        <div className="flex items-center justify-between gap-2 rounded border border-dashed px-3 py-2">
          <span className="text-xs text-muted-foreground">
            <T context="task page default columns editor, no custom columns configured">
              Using the built-in columns.
            </T>
          </span>
          <Button variant="outline" size="sm" onClick={customizeFromBuiltIn}>
            <T context="button, start editing a task page's default columns">
              Customize Columns
            </T>
          </Button>
        </div>
      ) : (
        <>
          <ul className="flex flex-col gap-1">
            {value.map((column, index) => (
              <DefaultColumnRow
                key={column.id}
                column={column}
                index={index}
                count={value.length}
                onMove={(delta) =>
                  onChange(moveDefaultColumn(value, index, delta))
                }
                onUpdate={(updates) =>
                  onChange(updateDefaultColumn(value, column.id, updates))
                }
                onRemove={() =>
                  onChange(value.filter((c) => c.id !== column.id))
                }
              />
            ))}
          </ul>
          {value.length === 0 && (
            <p className="text-[11px] text-destructive">
              <T context="validation hint in the task page default columns editor">
                Add at least one column, or switch back to the built-in columns.
              </T>
            </p>
          )}
          <div className="flex items-center gap-2">
            <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" disabled={!taskType}>
                  <PlusIcon className="h-4 w-4" />
                  <T context="button, add a custom column to the task queue table">
                    Add Column
                  </T>
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-72 p-2">
                <TaskColumnPicker
                  taskType={taskType}
                  onAdd={(column) => {
                    onChange([
                      ...value,
                      { ...column, id: newCustomColumnId() },
                    ]);
                    setPickerOpen(false);
                  }}
                />
              </PopoverContent>
            </Popover>
            <Button variant="ghost" size="sm" onClick={() => onChange(null)}>
              <T context="button, discard a task page's custom default columns">
                Use Built-in Columns
              </T>
            </Button>
          </div>
        </>
      )}
      <p className="text-[11px] text-muted-foreground">
        <T context="hint under the task page default columns field">
          Columns everyone sees on this page until they change their own column
          settings. Columns can read the task input, correction, outcome data,
          metadata, or the record the task is anchored to.
        </T>
      </p>
    </div>
  );
}

/** Whether users may change this page's columns (default: yes). */
export function TaskPageColumnCustomizationToggle({
  id,
  checked,
  onChange,
}: {
  id: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <Switch id={id} checked={checked} onCheckedChange={onChange} />
        <Label htmlFor={id} className="font-normal">
          <T context="task page setting toggle label">
            Let users customize columns
          </T>
        </Label>
      </div>
      <p className="text-[11px] text-muted-foreground">
        {checked ? (
          <T context="hint under the task page column customization toggle, when on">
            Users can hide, reorder, and add columns for themselves. New default
            columns still appear for them.
          </T>
        ) : (
          <T context="hint under the task page column customization toggle, when off">
            Everyone sees the default columns, and the queue hides its columns
            menu.
          </T>
        )}
      </p>
    </div>
  );
}
